import { useEffect, useState } from "react";
import Button from "../ui/Button";
import { useBidStore } from "../../store/useBidStore";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import { Edit2 } from "lucide-react";
import { DOWNLOAD_BASE_URL } from "../../lib/api";
import { axiosInstance } from "../../lib/axios";

const statusOptions = [
  { value: "Bid Submitted", label: "Bid Submitted" },
  { value: "Awarded Directly", label: "Awarded Directly" },
  { value: "In Negotiation", label: "In Negotiation" },
  { value: "Successfully Converted", label: "Successfully Converted" },
  { value: "Declined / Not Accepted", label: "Declined / Not Accepted" },
  { value: "Hired Other Freelancer", label: "Hired Other Freelancer" },
];

const BidQuickViewDrawer = ({ bid, isOpen, onClose, onUpdate }: any) => {
  const { updateBid, deleteBid } = useBidStore();
  const [form, setForm] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  // Full record: populated account/platform/BD + the connect ledger.
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    setForm(bid ? { ...bid } : null);
    setEditing(false);
    setDetails(null);
    if (bid?._id && isOpen) {
      axiosInstance
        .get(`/bids/${bid._id}/details`)
        .then(({ data }) => setDetails(data))
        .catch(() => setDetails(null));
    }
  }, [bid, isOpen]);

  if (!bid) return null;

  const full = details?.bid || bid;
  const account = full.freelanceAccount;
  const platformName = account?.platform?.name || "Upwork";
  const isDirect = (form?.entryType || full.entryType) === "Directly Awarded";
  const isInvite = (form?.entryType || full.entryType) === "Invitation";
  const totalConnects = full.connectsRefunded || isDirect
    ? 0
    : (isInvite ? 0 : Number(full.connectsUsed) || 0) + (full.boostUsed ? Number(full.boostConnects) || 0 : 0);
  const Section = ({ title, children }: any) => (
    <div className="rounded-xl border border-slate-200 p-4">
      <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">{title}</h4>
      {children}
    </div>
  );
  const Row = ({ label, children }: any) => (
    <div>
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-sm text-gray-800">{children ?? "—"}</div>
    </div>
  );

  const handleSave = async () => {
    try {
      await updateBid(bid._id, {
        jobLink: form.jobLink,
        bidAmount: form.bidAmount,
        bidStatus: form.bidStatus,
        connectsUsed: Number(form.connectsUsed) || 0,
        notes: form.notes,
        proposalLink: form.proposalLink,
      });
      onUpdate?.();
      setEditing(false);
    } catch {

    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this bid? Connects will be returned.")) return;
    await deleteBid(bid._id);
    onClose();
    onUpdate?.();
  };

  return (

    <div
      className={`fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl transform transition-transform duration-300 z-50 ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between p-6 border-b">
        <h3 className="text-lg font-semibold">Bid Details</h3>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => onClose()}>
            Close
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </div>

      <div className="p-6 space-y-4 overflow-y-auto h-[calc(100%-7rem)]">
        {form && (
          <>
            {/* ── Opportunity ──────────────────────────────────────────── */}
            <Section title="Opportunity">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Row label="Job Title">{full.jobTitle || "Untitled"}</Row>
                </div>
                <div className="md:col-span-2">
                  <InputField
                    label="Job Link"
                    value={form.jobLink || ""}
                    onChange={(e) => setForm({ ...form, jobLink: e.target.value })}
                    disabled={!editing}
                  />
                </div>
                <Row label="Freelance Account">
                  {full.platformAccount}
                  <span className="ml-1.5 text-[11px] font-semibold text-slate-500">— {platformName}</span>
                  {account?.isActive === false && (
                    <span className="ml-1.5 text-[10px] font-bold text-red-500">(deactivated)</span>
                  )}
                </Row>
                <Row label="Job Type">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                      isDirect
                        ? "bg-teal-100 text-teal-700"
                        : isInvite
                        ? "bg-violet-100 text-violet-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {full.entryType || "New Job"}
                  </span>
                </Row>
                <Row label="BD / Assignee">
                  {full.createdBy?.fullName || account?.assignedBd?.fullName || "—"}
                </Row>
                <Row label="Client / Contact">{full.contactRef?.name || "—"}</Row>
              </div>
            </Section>

            {/* ── Bid Information ─────────────────────────────────────── */}
            <Section title="Bid Information">
              <div className="grid grid-cols-2 gap-3">
                <InputField
                  label="Bid Amount (USD)"
                  value={form.bidAmount || ""}
                  onChange={(e) => setForm({ ...form, bidAmount: e.target.value })}
                  disabled={!editing}
                />
                <Row label="Rate Type">{full.bidType || "Fixed"}</Row>
                <InputField
                  label="Base Connects"
                  value={isDirect || isInvite ? 0 : form.connectsUsed || 0}
                  onChange={(e) =>
                    setForm({ ...form, connectsUsed: Number(e.target.value) })
                  }
                  disabled={!editing || isDirect || isInvite}
                />
                <Row label="Submitted">
                  {new Date(full.submittedAt || full.createdAt || full.submissionTimestamp).toLocaleString()}
                </Row>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-700">Boost</span>
                  <span
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ring-1 ${
                      isDirect
                        ? "bg-slate-100 text-slate-400 ring-slate-200"
                        : full.boostUsed
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-slate-100 text-slate-500 ring-slate-200"
                    }`}
                  >
                    {isDirect ? "Not applicable" : full.boostUsed ? "Boosted" : "Organic"}
                  </span>
                </div>
                {full.boostUsed && !isDirect && (
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Row label="Boost Connects">+{full.boostConnects || 0}</Row>
                    <Row label="Position">{full.boostPosition || "—"}</Row>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-gray-200 pt-2">
                  <span className="text-sm text-gray-600">Total Connects</span>
                  <span className="text-base font-bold text-[#2462b2]">{totalConnects}</span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm text-gray-600">Connect Cost</span>
                  <span className="text-base font-bold text-rose-600">
                    ${Number(full.connectCost || 0).toLocaleString()}
                    {Number(full.connectRate) > 0 && (
                      <span className="ml-1 text-[11px] font-medium text-slate-400">@ ${full.connectRate}/connect</span>
                    )}
                  </span>
                </div>
                {full.connectsRefunded && (
                  <p className="text-[11px] text-amber-700 mt-1.5">Connects were refunded (job deleted) — excluded from account usage.</p>
                )}
              </div>

              {/* Connect ledger — one row per action, never duplicated */}
              {details?.transactions?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1.5">Connect Transactions</div>
                  <div className="space-y-1">
                    {details.transactions.map((t: any) => (
                      <div
                        key={t._id}
                        className={`flex items-center justify-between text-[12px] rounded-lg px-2.5 py-1.5 border ${
                          t.voided ? "border-slate-100 bg-slate-50 text-slate-400 line-through" : "border-slate-200 bg-white text-slate-700"
                        }`}
                      >
                        <span className="font-semibold uppercase text-[10px] tracking-wide">
                          {t.type === "boost" ? "Boost" : "Bid"} · {t.accountName}
                        </span>
                        <span>
                          {t.connects} connects · ${Number(t.cost || 0).toLocaleString()}
                          <span className="ml-1.5 text-slate-400">{new Date(t.createdAt).toLocaleDateString()}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* ── Status & Conversion ─────────────────────────────────── */}
            <Section title="Status & Conversion">
              <div className="grid grid-cols-1 gap-3">
                <SelectField
                  label="Status"
                  value={form.bidStatus}
                  onChange={(e) => setForm({ ...form, bidStatus: e.target.value })}
                  options={
                    isDirect
                      ? statusOptions.filter((s) => ["Awarded Directly", "Successfully Converted"].includes(s.value))
                      : statusOptions
                  }
                />
                {isDirect && (
                  <p className="text-[11px] text-teal-700 -mt-2">
                    Directly Awarded flow: Awarded Directly → Successfully Converted.
                  </p>
                )}
                <InputField
                  label="Proposal Link"
                  value={form.proposalLink || ""}
                  onChange={(e) =>
                    setForm({ ...form, proposalLink: e.target.value })
                  }
                  disabled={!editing}
                />
                {full.projectRef && (
                  <Row label="Converted Project">
                    {full.projectRef?.name || "Linked project"}
                  </Row>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Row label="Created">{new Date(full.createdAt).toLocaleString()}</Row>
                  <Row label="Last Updated">{full.updatedAt ? new Date(full.updatedAt).toLocaleString() : "—"}</Row>
                </div>
              </div>
            </Section>

            {/* ── Communication ───────────────────────────────────────── */}
            <Section title="Communication">
              <label className="block text-sm text-gray-500 mb-1">
                Notes / Client Communication
              </label>
              <textarea
                className="w-full p-3 border rounded-xl h-28"
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                disabled={!editing}
              />
            </Section>

            {/* ── Attachments ─────────────────────────────────────────── */}
            <Section title="Attachments">
              <div className="space-y-2">
                {full.screenshots?.length ? (
                  full.screenshots.map((s: string, idx: number) => (
                    <a
                      key={idx}
                      href={`${DOWNLOAD_BASE_URL}${s.replace(/^\/+/, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-[#2462b2] hover:underline block"
                    >
                      {s.split("/").pop()}
                    </a>
                  ))
                ) : (
                  <div className="text-sm text-gray-400">No attachments</div>
                )}
              </div>
            </Section>
          </>
        )}
      </div>

      <div className="p-4 border-t flex justify-end gap-3 bg-white">
        {!editing ? (
          <Button variant="primary" onClick={() => setEditing(true)}>
            <Edit2 size={14} /> Edit
          </Button>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setForm(bid);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        )}
      </div>
   </div>

  );
};

export default BidQuickViewDrawer;
